import * as errors from '@apps-common/js-client/api/errors';
import {createAction, createAsyncThunk} from '@reduxjs/toolkit';

import {getOrderById} from '../selectors/orders.js';
import {getFolderNameFromOrder, orderIsFolder} from '../util/folders.js';
import {unauthorizedUser} from './errors.js';
import * as notificationActions from './notifications.js';
import {getOrders} from '@explorer/api/orders';
import {
  ORDERS_LONG_POLLING_INTERVAL,
  ORDERS_SHORT_POLLING_INTERVAL,
  ORDER_STATES,
} from '@explorer/constants';
import {isSentinelHubOrder} from '@explorer/util/orders.js';

export const reset = createAction('RESET_ORDERS');

let timer = null;

export const stopPolling = () => () => clearInterval(timer);

export const pollOrders = pollInterval => dispatch => {
  dispatch(stopPolling());
  timer = setInterval(
    () => dispatch(fetchOrdersRequest()), // eslint-disable-line
    pollInterval
  );
};

export const fetchOrdersRequest = createAsyncThunk(
  'orders/fetch',
  (url, {dispatch, getState, rejectWithValue}) => {
    const oldState = getState();
    dispatch(stopPolling());
    return getOrders(url)
      .then(data => {
        const orders = data ? data.orders.filter(order => !!order.name) : [];

        if (orders.length) {
          const isOrderQueued = orders.some(
            order => order.state === ORDER_STATES.QUEUED
          );

          // Look for orders in the background, speed it up when
          //  there is an order queued.
          if (isOrderQueued) {
            dispatch(pollOrders(ORDERS_SHORT_POLLING_INTERVAL));
          } else {
            dispatch(pollOrders(ORDERS_LONG_POLLING_INTERVAL));
          }
        }

        // for any order that has its status change to 'success', show a
        // notification
        orders.forEach(newOrderObj => {
          const oldOrderObj = getOrderById(newOrderObj.id, oldState);
          if (
            oldOrderObj &&
            oldOrderObj.state !== 'success' &&
            newOrderObj.state === 'success'
          ) {
            const notification = {
              title: {
                i18nKey: 'ordersList.orderReadyTitle',
                orderName: newOrderObj.name,
              },
              message: {
                i18nKey: 'ordersList.orderReadyMessage',
              },
            };

            // check to see if this is a folder-order
            if (orderIsFolder(newOrderObj)) {
              const folderName = getFolderNameFromOrder(newOrderObj.name);
              notification.title = '';
              notification.message = {
                i18nKey: 'ordersList.orderReadyFolderMessage',
                folderName,
              };
            }

            if (isSentinelHubOrder(newOrderObj)) {
              notification.title = `Order "${newOrderObj.name}" is ready`;
              notification.message =
                'Open the "My Orders" sidebar to the left to view your orders.';
            }

            dispatch(notificationActions.notify(notification));
          }
        });

        return {orders, nextPage: data._links.next || null};
      })
      .catch(err => {
        dispatch(stopPolling());
        if (err instanceof errors.Unauthorized) {
          dispatch(unauthorizedUser(err));
        }
        return rejectWithValue(
          `Request failed with status code ${err.response.status}`
        );
      });
  }
);

// This should probably go into a util file and not here
export const getErrorNotification = (err, orderName, bundleName) => {
  const notification = {
    title: {
      i18nKey: 'ordersList.errorTitle',
      orderName,
    },
    message: {
      i18nKey: bundleName
        ? 'ordersList.errorMessageWithBundle'
        : 'ordersList.errorMessageNoBundle',
      bundleName,
    },
    severity: 'error',
    persist: true,
  };

  const statusCode = err.response?.status;

  if (statusCode === 409) {
    notification.message = {i18nKey: 'ordersList.errorMessageTooManyOrders'};
  } else if (statusCode === 400 || statusCode === 403) {
    if (err.body.field) {
      notification.message = err.body.field.Details[0].message;
    } else {
      notification.message = err.body.general
        ? err.body.general[0].message
        : err.body.message;
    }
  }

  return notification;
};
